'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { GENERATE_PHOTO_BY_PHOTO } from '@/.graphql';
import { GenerateButton } from '@/src/components/GenerateButton';
import { Header } from '@/src/components/Header';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
  useMobileContext,
  useImageGenerationContext,
} from '@/src/providers';
import { PublicationFile } from '@/src/utils/serializeFile';
import { getSource } from '@/src/utils/getSource';

import { CloseAppButton } from '../_generate_image/CloseAppButton';
import { UploadButton } from '../_generate_image/UploadButton';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center',
  wrapper: 'shrink-0 bg-fill-menu',
  imageWrap:
    'relative shrink-0 flex flex-col items-center pb-9 mb-5 justify-end w-full aspect-square',
  image:
    'absolute w-full h-full top-0 left-0 object-cover object-top pointer-events-none',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-xl',
  name: 'relative text-2xl font-bold mb-3',
  innerWrap: 'relative p-5',
  info: 'flex flex-col gap-4 p-4 mb-4 bg-fill-quinary rounded-xl',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
  carouselContainer: 'relative overflow-hidden w-full aspect-square',
  carousel: 'flex duration-700 cursor-pointer',
};

export const RepeatFeedPhotoPortal: FC = () => {
  const t = useTranslations('imageModal');
  const { genderMode, prevPortal } = useStyleContext();
  const { activeImage, activeImageFile } = useImageGenerationContext();
  const sendGTM = useGTMContext();
  const profile = useProfileContext()!;
  const { closePortal, openPortal } = usePortalContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [photoByPhoto, { loading }] = useMutation(GENERATE_PHOTO_BY_PHOTO, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });
  const { isMobiles } = useMobileContext();
  const [userImage, setUserImage] = useState<File | null>(null);
  const toastSuccessPhotoAddedIdRef = useRef<string | number | null>(null);

  const handleUploadButtonClick = () => {
    sendGTM({
      event: 'upload_repeat_photo',
      type: null,
      block: 'Фото по фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: null,
      gender: genderMode,
      source: getSource(prevPortal),
    });
  };

  const handleUserFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';
    setUserImage(file);

    sendGTM({
      event: 'upload_repeat_photo_complete',
      type: null,
      block: 'Фото по фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: null,
      source: 'Feed',
      gender: genderMode,
    });

    if (toastSuccessPhotoAddedIdRef.current) {
      toast.dismiss(toastSuccessPhotoAddedIdRef.current);
      toastSuccessPhotoAddedIdRef.current = null;
    }

    toastSuccessPhotoAddedIdRef.current = toast(t('successUserPhotoAdded'), {
      autoClose: 3000,
      position: 'bottom-center',
      icon: (
        <Image
          width={16}
          height={18}
          src="/icons/check.svg"
          alt="check"
          className="w-4 h-4"
        />
      ),
      theme: 'dark',
      style: {
        backgroundColor: '#3D3E41',
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '500',
        borderRadius: '12px',
        padding: '16px',
        bottom: '32px',
        maxWidth: 'calc(100% - 40px)',
        alignItems: 'start',
      },
    });
  };

  const handleClickGenerate = async () => {
    try {
      setButtonDisabled(true);

      sendGTM({
        event: 'Repeat_publisher',
        type: null,
        block: 'Фото по фото',
        publisher: (activeImage as PublicationFile).publisherId,
        source: 'Feed',
        gender: genderMode,
      });

      if (toastSuccessPhotoAddedIdRef.current) {
        toast.dismiss(toastSuccessPhotoAddedIdRef.current);
        toastSuccessPhotoAddedIdRef.current = null;
      }

      const { data } = await photoByPhoto({
        variables: {
          referenceImage: activeImageFile,
          userImage,
          publicationId: (activeImage as PublicationFile).publicationId,
        },
      });

      // TO-DO: add request to increase photo reuse counter

      if (!data) {
        throw new Error('No data');
      }

      const queueTime = data.photoByPhoto.queueTime;

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="check"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Repeat_publisher_complete',
        type: null,
        block: 'Фото по фото',
        publisher: (activeImage as PublicationFile).publisherId,
        source: 'Feed',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setUserImage(null);
      }, 5000);
    } catch (err) {
      const error = err as Error;
      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Repeat_publisher_error',
        type: null,
        block: 'Фото по фото',
        publisher: (activeImage as PublicationFile).publisherId,
        source: 'Feed',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setUserImage(null);
      }, 5000);
    }
  };

  const handleClosePortal = () => {
    if (prevPortal === 'allTools') {
      closePortal();
      openPortal(PORTALS.AllTools);
    } else {
      closePortal();
    }
  };

  const disabled = buttonDisabled || loading;

  return (
    <PortalModal
      portalId={PORTALS.RepeatFeedPhoto}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>

        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.carouselContainer}>
              <div className={styles.carousel}>
                <div className={styles.imageWrap}>
                  <Image
                    src="/images/inpaint-bg.webp"
                    width={826}
                    height={826}
                    quality={95}
                    priority
                    alt=""
                    className="pointer-events-none block absolute z-0 size-full translate-z-0 object-cover select-none"
                  />
                  <Image
                    src={activeImage.src}
                    width={826}
                    height={826}
                    quality={100}
                    priority
                    alt=""
                    className="pointer-events-none block size-full translate-z-0 absolute z-1 object-contain select-none"
                  />
                </div>
              </div>
            </div>

            <div className={styles.innerWrap}>
              <div className={styles.info}>
                <div>
                  <div className={styles.infoTitle}>{t('info.title')}</div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.photoByPhotoText')}
                    </li>
                  </ul>
                </div>
              </div>

              {profile?.isAcitveStartFunnel ? (
                <CloseAppButton
                  text={t('buttonPay')}
                  keybord="paymentDownSale4"
                />
              ) : !userImage ? (
                <UploadButton
                  disabled={disabled}
                  isMultiple={false}
                  buttonText={t('buttonUploadUserPhoto')}
                  onChange={handleUserFileChange}
                  onClick={handleUploadButtonClick}
                />
              ) : (
                <GenerateButton
                  disabled={disabled}
                  onClick={handleClickGenerate}
                  text={t('buttonRepeatPhoto')}
                  price="5"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
